import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'chatInputContinueAssistiveLabel'
	| 'chatInputContinueConversationPlaceholder'
	| 'chatInputDefaultPlaceholder'
	| 'chatInputDefaultPlaceholderUpdated'
	| 'chatInputTaskModePlaceholder'
	| 'chatInputDisabledAgentTooltip'
	| 'chatInputAddSourcesButton'
	| 'deepResearchPlaceholder'
	| 'queuedHeaderLabel'
	| 'queuedMessageEmptyPreview'
	| 'queuedMessageRemoveLabel';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		chatInputContinueAssistiveLabel: {
			id: 'ai-mate.chat-input.chat-input-continue-assistive-label',
			defaultMessage: 'Ask follow up',
			description: 'Assistive label for continuing a conversation with chat input',
		},
		chatInputContinueConversationPlaceholder: {
			id: 'ai-mate.chat-input.chat-input-continue-conversation-placeholder',
			defaultMessage: 'Describe what you want to know or do next',
			description: 'Placeholder for continuing a conversation with chat input',
		},
		chatInputDefaultPlaceholder: {
			id: 'ai-mate.chat-input.chat-input-default-placeholder',
			defaultMessage: 'Ask, @mention, or / for actions',
			description:
				'Placeholder text shown inside the chat input field before the user starts typing.',
		},
		chatInputDefaultPlaceholderUpdated: {
			id: 'ai-mate.chat-input.chat-input-default-placeholder.updated',
			defaultMessage: 'Write a prompt, @mentions someone, or use / for actions',
			description:
				'Updated placeholder text shown in the chat input field, guiding the user to write a prompt, mention someone, or use slash commands.',
		},
		chatInputTaskModePlaceholder: {
			id: 'ai-mate.chat-input.chat-input-task-mode-placeholder',
			defaultMessage: 'Help me create...',
			description:
				'Placeholder text shown inside the chat input field when Max mode (task mode) is active. Prompts the user to describe what they want Rovo to autonomously create or accomplish.',
		},
		chatInputDisabledAgentTooltip: {
			id: 'ai-mate.chat-input.chat-input-disabled-agent-tooltip',
			defaultMessage: 'Only one agent can be active at a time',
			description:
				'Tooltip text shown when hovering or focusing a disabled agent mention chip in the chat input. Displayed as the content of an interactive tooltip popover anchored to the chip. The chip appears greyed out because the user has @-mentioned multiple agents in the same prompt but only the most recently mentioned agent is currently active. The purpose is to explain why the chip is non-interactive and disabled, so users understand that the system intentionally limits the conversation to one agent at a time.',
		},
		chatInputAddSourcesButton: {
			id: 'ai-mate.chat-input.add-sources-button',
			defaultMessage: 'Add sources',
			description:
				'Button text that opens a source picker so the user can add knowledge sources to the current Rovo Space.',
		},
		deepResearchPlaceholder: {
			id: 'ai-mate.chat-input.deep-research-placeholder',
			defaultMessage: 'Get a detailed report',
			description:
				'Placeholder text shown in the chat input when Deep Research mode is active from the add menu. Used as the editor placeholder to prevent the animated placeholder prompts from cycling, which would otherwise open the skills typeahead on Enter.',
		},
		queuedHeaderLabel: {
			id: 'ai-mate.chat-input.queued-header-label',
			defaultMessage: '{count, plural, one {# queued message} other {# queued messages}}',
			description:
				'Header text for the collapsible queued messages panel above the chat input. The count is the number of messages waiting to be sent after Rovo finishes responding.',
		},
		queuedMessageEmptyPreview: {
			id: 'ai-mate.chat-input.queued-message-empty-preview',
			defaultMessage: 'Empty message',
			description:
				'Fallback preview text for a queued chat message when no readable message content can be shown.',
		},
		queuedMessageRemoveLabel: {
			id: 'ai-mate.chat-input.queued-message-remove-label',
			defaultMessage: 'Remove queued message: {preview}',
			description:
				'Accessible label for the icon button that removes one message from the queued messages panel. The {preview} placeholder is the visible queued message preview, or the fallback empty message label when no readable message content can be shown.',
		},
	});

export default message;
